import {
  AgGridTextTooltip,
  isTerminalQuoteStatus,
  PopoverContent,
  QuoteStatusEnum,
  Stack,
  Text,
  type ColumnDef,
  type CountOfParams,
  type CountOfValue,
  type QuoteMarket,
} from '@talos/kyoko';
import type { ICellRendererParams, ITooltipParams } from 'ag-grid-community';
import { QuoteRow, SweepGroupIDRow, type QuotesBlotterRow } from './types';

export function getQuotesV2ResponsesColumn(): ColumnDef<QuotesBlotterRow> {
  return {
    type: 'countOf',
    id: 'responses',
    title: 'Responses',
    description: 'Count of responses received from makers.',
    initialWidth: 50,
    params: {
      getItems: getResponsesColumnItems,
      shouldCount: (market: QuoteMarket) =>
        market.QuoteStatus === QuoteStatusEnum.Open || isTerminalQuoteStatus(market.QuoteStatus),
    } satisfies CountOfParams<QuotesBlotterRow, QuoteMarket>,
    tooltipValueGetter: ({ value }: ITooltipParams<unknown, CountOfValue<QuoteMarket>>) => value,
    tooltipComponent: (props: ICellRendererParams<unknown, CountOfValue<QuoteMarket> | string>) => {
      if (!props.value) {
        return null;
      }

      if (typeof props.value === 'string') {
        // The headerTooltip ("description") above also uses the column's tooltipComponent, so we have to support this here...
        return <AgGridTextTooltip {...props} />;
      }

      return (
        <PopoverContent>
          <Stack maxWidth="300px" color="colorTextDefault" gap="spacing4">
            <Text>
              Response:{' '}
              {props.value.countedItems
                .map((qm: QuoteMarket) => props.context.current.marketDisplayNameByName?.get(qm.Market) ?? qm.Market)
                .join(', ')}
            </Text>

            <Text>
              No Response:{' '}
              {props.value.notCountedItems
                .map((qm: QuoteMarket) => props.context.current.marketDisplayNameByName?.get(qm.Market) ?? qm.Market)
                .join(', ')}
            </Text>
          </Stack>
        </PopoverContent>
      );
    },
  };
}

// Helper function to resolve the responses count (eg 0 / 3, 2 / 4) from a quote blotter row of some kind
function getResponsesColumnItems(data: QuotesBlotterRow): QuoteMarket[] | undefined {
  if (data instanceof SweepGroupIDRow) {
    if (data.SweepGroupID === SweepGroupIDRow.unassignedValue) {
      // We're in the "None" group
      return undefined;
    }

    const hasFilledQuote = data.quotes.some(
      // Todo use the new quote.isFilled getter when that exists. Does the same thing, but centralized
      quote => quote.QuoteStatus === QuoteStatusEnum.PendingFix || quote.QuoteStatus === QuoteStatusEnum.Filled
    );
    if (hasFilledQuote) {
      // If there is any filled quote in the sweep group, then the sweep group id row has "done its job", we dont try to show the count of responders anymore
      return undefined;
    }

    const allQuotesTerminal = data.quotes.every(quote => quote.isTerminal);
    if (allQuotesTerminal) {
      // if we are a Sweep group, and all our children are terminal (eg filled, canceled, rejected), then we don't show any count
      return undefined;
    }

    // Else, let through all the underlying quote markets and count the items
    return data.quotes.flatMap(quote => quote.Markets);
  }

  if (data instanceof QuoteRow && !data.quote.isTerminal) {
    return data.quote.Markets;
  }

  return undefined;
}
